import React, { useState } from 'react';
import { Grid, IconButton, Typography } from '@material-ui/core';

import { Button, DataGrid, DataGridCell, DesktopPage, DisplayQr, Loader } from 'ui';
import { MintTokensModal } from 'app/desktopMintTokens/mintTokensModal/MintTokensModal';
import { TransferTokensModal } from 'app/desktopMintTokens/transferTokensModal/TransferTokensModal';
import { CopyIcon, CopyQrIcon } from 'ui/icons';
import { useLocale } from 'hooks/useLocale/useLocale';
import { DataGridColumnConfig } from 'ui/dataGrid/DataGrid.types';

import { useStyles } from './DesktopMintTokens.styles';
import { DesktopMintTokensProps, TokensGrid } from './DesktopMintTokens.types';

export const DesktopMintTokens = ({ tokens, transferTokens, mintTokens, isLoading }: DesktopMintTokensProps) => {
  const classes = useStyles();
  const { formatMessage } = useLocale();
  const [isMintModalOpened, setMintModalOpened] = useState(false);
  const [isTransferModalOpened, setTransferModalOpened] = useState(false);
  const [tokenId, setTokenId] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const columnsConfig: DataGridColumnConfig<TokensGrid>[] = [
    {
      name: 'id',
      labelId: 'desktop.mint.token_id',
      formatCell: data => {
        return (
          <DataGridCell
            title={
              <div className={classes.iconContainer}>
                <Typography className={classes.title} data-testid="cell-title">
                  {data.id}
                </Typography>
                <IconButton
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCopy(data.id);
                  }}
                  size="small"
                >
                  <CopyIcon className={classes.icon} />
                </IconButton>
                <IconButton
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTokenId(data.id);
                  }}
                  size="small"
                >
                  <CopyQrIcon className={classes.icon} />
                </IconButton>
              </div>
            }
          />
        );
      },
    },
    {
      name: 'isOwnershipTransferred',
      labelId: 'desktop.mint.is_transferred',
      formatCell: data =>
        formatMessage({
          id: data.isOwnershipTransferred ? 'desktop.mint.transferred' : 'desktop.mint.not_transferred',
        }),
    },
    {
      name: 'isUsed',
      labelId: 'desktop.mint.is_used',
      formatCell: data =>
        formatMessage({
          id: data.isUsed ? 'desktop.mint.used' : 'desktop.mint.not_used',
        }),
    },
  ];

  return (
    <>
      <DesktopPage>
        <Grid container className={classes.buttonContainer} spacing={2}>
          <Grid item>
            <Button variant="outlined" color="primary" onClick={() => setMintModalOpened(true)} size="small">
              {formatMessage({ id: 'desktop.mint.mint' })}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => setTransferModalOpened(true)} size="small">
              {formatMessage({ id: 'desktop.mint.transfer' })}
            </Button>
          </Grid>
        </Grid>

        <Grid container>
          {isLoading ? (
            <Loader suppressText />
          ) : (
            <DataGrid<TokensGrid> columnsConfig={columnsConfig} data={tokens} hideCsvButton />
          )}
        </Grid>
      </DesktopPage>
      {isMintModalOpened && (
        <MintTokensModal
          isOpened={isMintModalOpened}
          handleClose={() => setMintModalOpened(false)}
          onSubmit={mintTokens}
        />
      )}
      {isTransferModalOpened && (
        <TransferTokensModal
          isOpened={isTransferModalOpened}
          handleClose={() => setTransferModalOpened(false)}
          onSubmit={transferTokens}
          tokens={tokens.filter(b => !b.isOwnershipTransferred).map(b => b.id)}
        />
      )}
      {tokenId && <DisplayQr isOpened={!!tokenId} handleClose={() => setTokenId(null)} value={tokenId} />}
    </>
  );
};
